
import React, { useState, useEffect } from "react";
import { useFormContext, Controller } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { Cloud, Compass, Ruler, Settings, CreditCard } from "lucide-react";
import { concessionarias, tiposEstrutura, nomesEstrutura } from "@/types";
import { InstalacaoFormData } from "./tabs/InstalacaoTab";
import { InstallationControlSection } from "./sections/InstallationControlSection";

export const InstalacaoConfigForm = () => {
  const { control, watch, setValue, getValues } = useFormContext<InstalacaoFormData>();
  const [novaConcessionaria, setNovaConcessionaria] = useState("");

  // Watch form values
  const difusaoAzimutal = watch("difusaoAzimutal");
  const inclinacaoTelhado = watch("inclinacaoTelhado");
  const concessionariasWatch = watch("concessionarias");
  const estruturasWatch = watch("estruturas");

  // Load existing config data
  useEffect(() => {
    const loadExistingConfig = () => {
      // Load from localStorage
      const savedConfig = localStorage.getItem("instalacaoConfigData");
      if (savedConfig) {
        try {
          const parsed = JSON.parse(savedConfig);
          Object.keys(parsed).forEach(key => {
            setValue(key as any, parsed[key]);
          });
        } catch (error) {
          console.error("Erro ao carregar configurações de instalação:", error);
        }
      }

      // Load default concessionárias from the app if none are set
      if (!concessionariasWatch?.length) {
        setValue("concessionarias", concessionarias);
      }
      
      // Load default estruturas if none are set
      if (!estruturasWatch?.length) {
        setValue("estruturas", tiposEstrutura.map(tipo => ({
          tipo,
          nome: nomesEstrutura[tipo] || tipo
        })));
      }
    };

    loadExistingConfig();
  }, [setValue, concessionariasWatch, estruturasWatch]);

  // Add new concessionária
  const addConcessionaria = () => {
    if (!novaConcessionaria.trim()) {
      toast.error("Digite o nome da concessionária");
      return;
    }

    if (concessionariasWatch.includes(novaConcessionaria)) {
      toast.error("Esta concessionária já existe");
      return;
    }

    setValue("concessionarias", [...concessionariasWatch, novaConcessionaria]);
    setNovaConcessionaria("");
    toast.success("Concessionária adicionada");
  };

  // Delete concessionária
  const deleteConcessionaria = (conc: string) => {
    setValue(
      "concessionarias", 
      concessionariasWatch.filter(c => c !== conc)
    );
    toast.success("Concessionária removida");
  };

  return (
    <Card className="shadow-md mb-8">
      <CardHeader>
        <CardTitle className="text-xl font-bold text-green-700 flex items-center gap-2">
          <Cloud className="h-6 w-6" /> Configuração dos Dados de Instalação
        </CardTitle>
        <CardDescription>
          Configure os parâmetros técnicos padrão para os cálculos de instalação solar
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Concessionárias Section */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium">Concessionárias</h3>
          <div className="flex gap-2">
            <Input
              placeholder="Nome da concessionária"
              value={novaConcessionaria}
              onChange={(e) => setNovaConcessionaria(e.target.value)}
              className="flex-1"
            />
            <Button onClick={addConcessionaria} className="bg-green-600 hover:bg-green-700">
              Adicionar
            </Button>
          </div>

          <Controller 
            name="concessionarias"
            control={control}
            render={({ field }) => (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 mt-4">
                {field.value.map((conc) => (
                  <div key={conc} className="flex justify-between items-center bg-gray-50 p-2 rounded">
                    <span>{conc}</span>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                      onClick={() => deleteConcessionaria(conc)}
                    >
                      &times;
                    </Button>
                  </div>
                ))}
              </div>
            )}
          />
        </div>

        <Separator className="my-4" />

        {/* Tarifas Section */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-green-700 flex items-center gap-2">
            <CreditCard className="h-5 w-5" /> 
            Configuração de Tarifas
          </h3>
          
          <div className="bg-green-50 p-4 rounded-lg">
            <p className="text-sm text-gray-700 mb-4">
              Configure os tipos de tarifas disponíveis por tipologia de cliente. 
              Isso será usado na calculadora solar para guiar o usuário nas seleções.
            </p>
            
            <div className="space-y-2">
              <Label className="text-sm font-medium">Tipos de Tensão</Label>
              <Controller
                name="tiposTensao"
                control={control}
                render={({ field }) => (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {field.value.map((tipo, index) => (
                      <div key={index} className="bg-white p-2 rounded border border-gray-200">
                        <span className="font-medium">{tipo.label}</span>
                        <span className="text-xs text-gray-500 block">Valor: {tipo.value}</span>
                      </div>
                    ))}
                  </div>
                )}
              />
            </div>
            
            <div className="space-y-2 mt-4">
              <Label className="text-sm font-medium">Tipologias</Label>
              <Controller
                name="tipologias"
                control={control}
                render={({ field }) => (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {field.value.map((tipo, index) => (
                      <div key={index} className="bg-white p-2 rounded border border-gray-200">
                        <span className="font-medium">{tipo.label}</span>
                        <span className="text-xs text-gray-500 block">Valor: {tipo.value}</span>
                      </div>
                    ))}
                  </div>
                )}
              />
            </div>
          </div>
        </div>

        {/* Difusão Azimutal e Inclinação Section */}
        <div className="space-y-4 p-4 bg-green-50 rounded-lg">
          <h3 className="text-lg font-medium text-green-800 flex items-center gap-2">
            <Compass className="h-5 w-5" /> 
            Configurações de Orientação
          </h3>
          
          <div className="space-y-6">
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Label className="text-sm font-medium">
                  Difusão Azimutal (graus)
                </Label>
                <span className="text-sm font-medium bg-white px-2 py-1 rounded-md">
                  {difusaoAzimutal}°
                </span>
              </div>
              <Controller
                name="difusaoAzimutal"
                control={control}
                render={({ field }) => (
                  <Slider
                    value={[field.value]}
                    min={0}
                    max={360}
                    step={1}
                    onValueChange={(value) => field.onChange(value[0])}
                    className="my-2"
                  />
                )}
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>0°</span>
                <span>180°</span>
                <span>360°</span>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Label className="text-sm font-medium">
                  Inclinação do Telhado (graus)
                </Label>
                <span className="text-sm font-medium bg-white px-2 py-1 rounded-md">
                  {inclinacaoTelhado}°
                </span>
              </div>
              <Controller
                name="inclinacaoTelhado"
                control={control}
                render={({ field }) => (
                  <Slider
                    value={[field.value]}
                    min={0}
                    max={45}
                    step={1}
                    onValueChange={(value) => field.onChange(value[0])}
                    className="my-2"
                  />
                )}
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>0°</span>
                <span>22.5°</span>
                <span>45°</span>
              </div>
            </div>
          </div>
        </div>

        {/* Tipos de Telhado Section */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium flex items-center gap-2">
            <Ruler className="h-5 w-5" /> 
            Tipos de Telhado
          </h3>
          
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600 mb-4">
              Os tipos de telhado estão configurados na seção de estruturas. 
              Acesse a configuração de estruturas para adicionar ou modificar tipos de telhado.
            </p>
            
            <Controller
              name="estruturas"
              control={control}
              render={({ field }) => (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                  {field.value.map((estrutura) => (
                    <div key={estrutura.tipo} className="bg-white p-2 rounded border border-gray-200">
                      <span className="font-medium">{estrutura.nome}</span>
                    </div>
                  ))}
                </div>
              )}
            />
          </div>
        </div>

        {/* Installation Control Section */}
        <InstallationControlSection />
      </CardContent>
      <CardFooter>
        <Button 
          type="submit"
          size="lg"
          className="w-full bg-green-600 hover:bg-green-700"
        >
          Salvar Configurações
        </Button>
      </CardFooter>
    </Card>
  );
};
